import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BaseButtonComponent } from '../base-button';
import { Action, ActionType } from 'src/app/models/action';
import { CustomerType } from '../../../models/client-configuration';
import { Format, hiddenFormats, Record } from '../../../models/record';
import { CheckoutDownload } from '../../../models/checkout-download';
import { OleConfiguration } from '../../../models/ole-configuration';
import { BookClubCarousel } from '../../../models/bookclubcarousel';
import { ExperienceResult } from '../../../models/experience-result';
import { ExperienceLink } from '../../../models/experience-link';
import { ExperienceType } from '../../../models/experiences';
import { UrlTree } from '@angular/router';
import { RecaptchaDialogComponent } from '../../../_shared/components/dialogs/recaptcha-dialog/recaptcha-dialog.component';
import { Util } from 'src/app/util';
import { Hold } from 'src/app/state/user/holds/models/hold';
import { ExternalVideoModel } from 'src/app/_core/state/dialogs/external-video/model/external-video-data.model';

@Component({
  selector: 'app-checkout-button',
  templateUrl: './checkout-button.component.html',
  styleUrls: ['./checkout-button.component.scss', '../base-button.scss'],
})
export class CheckoutButtonComponent extends BaseButtonComponent {
  customerType: CustomerType;
  @Input()
  isMoreOption: string;

  @Input()
  recordIcon: string;

  @Input()
  recordId: string;

  @ViewChild(RecaptchaDialogComponent) recaptchaDialog!: RecaptchaDialogComponent;

  @Output()
  disableRecord: EventEmitter<boolean> = new EventEmitter<boolean>();

  private MAX_BOOK_CLUBS = 300;
  private ONLY_MODERATES = true;
  readonly ALLOWED_BOOKCLUBMODE_FORMATS: string[] = ['EPUB', 'PDF'];
  readonly HIDDEN_FORMATS: Format[] = hiddenFormats;

  private downloadFormat;

  defaultFormat: Format;

  ngOnInit() {
    this.customerType = this.configService.customerType;
    if (this.recordId != null) {
      this.record = { id: this.recordId };
    }

    this.record.formats.forEach((format) => {
      if (!this.defaultFormat && ![...this.HIDDEN_FORMATS].includes(format)) {
        this.defaultFormat = format;
      }
    });
  }

  doCheckout() {
    const action: Action = {
      type: ActionType.CHECKOUT,
      recordId: this.record.id,
    };

    this.doAction(
      () => {
        this.analyticsService.sendCheckoutStartEvent();
        this.checkoutsFacade.postCheckout(this.record.id, this.getCurrentScreen(), this.issueDate, this.rhId).subscribe(
          () => {
            const hold = this.holdsFacade.getHoldByRecordId(this.record.id, this.issueDate, this.rhId);

            if (hold) {
              const updatedHold: Hold = { ...hold, status: 'downloaded' };
              this.holdsFacade.setHold(updatedHold);
            }
            this.toastService.showToastSolution('messages.checkouts.new');

            if (this.configService.get().openResourceOnBorrow) {
              this.download();
            }

            this.analyticsService.sendCheckoutEndEvent();
          },
          (_err) => {
            this.waiting = false;
            if (_err.error.ERROR === 'ERROR_OD_FINDAWAY_LICENSE_EXPIRED') {
              this.disableRecord.emit(false);
            }
            throw _err;
          }
        );
      },
      action,
      true
    );
  }

  download(format?: string) {
    this.downloadFormat = format || this.defaultFormat;

    if (!this.record.container) {
      this.rhId = null;
    }

    if (this.rssItem != null) {
      this.rhId = this.rssItem.rhId;
    }

    // Check recaptcha
    if (this.configService.get().enableDrmCaptcha && this.downloadFormat === 'ACSM') {
      this.recaptchaDialog.openDialog();
      return;
    }

    this.internalDownload();
  }

  // Should show the modal if there are any experiences, and the bookclub mode is allowed
  private internalDownload(captchaToken?: string) {
    const checkout = this.checkoutsFacade.getCheckoutByRecordId(this.record.id, this.issueDate, this.rhId);
    if (!checkout) {
      return;
    }
    const token = this.extractToken(checkout.downloadUrl);
    const userId = this.userService.getUserId();

    this.apiService.getLearningExperiencesAssociatedToRecordId(this.record.id, userId).subscribe((exps) => {
      const learningExperienceId = Util.extractLearningExperienceId(exps);
      this.apiService
        .downloadResource(
          checkout.id,
          userId,
          this.downloadFormat,
          token,
          this.record.id,
          learningExperienceId,
          captchaToken
        )
        .subscribe(
          (response) => {
            if (this.shouldShowTheModal(exps, this.downloadFormat)) {
              this.apiService.getOleConfiguration(userId).subscribe((oleConfig) => {
                this.apiService.getBookClubs(userId, this.MAX_BOOK_CLUBS, this.ONLY_MODERATES).subscribe({
                  next: (bookClubsCollection) => {
                    this.handleLearningExperiences(exps, response, oleConfig, userId, bookClubsCollection);
                  },
                  error: () => {
                    this.handleLearningExperiences(exps, response, oleConfig, userId, []);
                  },
                });
              });
            } else {
              if (this.downloadFormat.includes('ACSM')) {
                this.checkoutsFacade.checkouts.find((_checkout) => _checkout.id === checkout.id).returnable = false;
              }

              if (this.downloadFormat.includes('HTML')) {
                return this.openDialog(response.redirect);
              }

              if (typeof response.redirect === 'string') {
                window.open().location.href = response.redirect;

                if (this.downloadFormat.includes('MP3_LH')) {
                  this.toastService.showToast('messages.checkouts.download', 'info');
                }
              }
            }
          },
          (err) => {
            throw err;
          }
        );
    });
  }

  // Should show the modal if there are any experiences, and the bookclub mode is allowed
  // and the element is either and epub or a pdf. Don't show if it's ACSM.
  private shouldShowTheModal(exps: Record[], format: string) {
    if (format && format.toUpperCase() === 'ACSM') {
      return false;
    }
    return (
      exps &&
      this.configService.get().showBookclubMode &&
      this.record.fileFormat &&
      this.ALLOWED_BOOKCLUBMODE_FORMATS.includes(this.record.fileFormat.toUpperCase())
    );
  }

  private handleLearningExperiences(
    exps: Record[],
    response: CheckoutDownload,
    oleConfig: OleConfiguration,
    userId: string,
    bookClubCollections: BookClubCarousel[]
  ) {
    const miniExps: ExperienceResult[] = exps.map((exp) => ({
      learningExperienceId: exp.learningExperienceId,
      title: exp.title,
      externalUrl: new URL(exp.externalLink).origin,
    }));
    miniExps.sort((first, second) => first.title.localeCompare(second.title));
    const links: ExperienceLink[] = this.getExperienceLinks(response, miniExps, oleConfig, bookClubCollections);
    if (links.length === 1) {
      const exp = links[0];
      window.open().location.href = exp.url;
    } else {
      this.bookClubHistoryService
        .getBookClubHistoryByUserIdAndResourceId(userId, this.getRecordId())
        .subscribe((bookClubHistory) => {
          if (bookClubHistory.some((history) => history.dontShow === true) || response.dontShowModal) {
            let experienceId = bookClubHistory.length
              ? bookClubHistory[bookClubHistory.length - 1].learningExperienceId
              : '';
            if (experienceId === 'plain') {
              experienceId = '';
            }
            const link: ExperienceLink = links.find((element) => element.learningExperienceId === experienceId);
            window.open().location.href = link.url;
          } else {
            this.bookclubLinksDialogFacade.openDialog(this.record.title, links, this.getRecordId());
          }
        });
    }
  }

  private getExperienceLinks(
    response: CheckoutDownload,
    experiencesArray: ExperienceResult[],
    oleConfig: OleConfiguration,
    bookClubCollections: BookClubCarousel[]
  ) {
    const moderateArray: BookClubCarousel[] = bookClubCollections.filter((bookClub) => bookClub.name === 'moderates');
    const links: ExperienceLink[] = [
      ...experiencesArray.map((exp) => ({
        name: exp.title,
        url: `${response.redirect}?bc=${exp.externalUrl}&c=${exp.learningExperienceId}`,
        learningExperienceId: exp.learningExperienceId,
        clicked: false,
        type: this.typeOfExperience(moderateArray, exp),
      })),
    ];
    if (this.isNormalUser(oleConfig, experiencesArray) || this.isCollaboratorUser(oleConfig, experiencesArray)) {
      return links;
    }
    if (experiencesArray.length > 1) {
      links.push({
        name: this.record.title,
        url: response.redirect as string,
        learningExperienceId: '',
        clicked: false,
        type: ExperienceType.PLAIN,
      });
    }
    return links;
  }

  private getRecordId() {
    if (this.availability === undefined) {
      return this.record.id;
    }
    return this.availability.recordId;
  }

  private typeOfExperience(
    moderateArray: BookClubCarousel[],
    exp: { externalUrl: string; learningExperienceId: string; title: string }
  ): ExperienceType {
    if (moderateArray.length === 0) {
      return ExperienceType.FOLLOWING;
    }
    const namesOfExperiences = moderateArray[0].bookClubs.map((experiences) => experiences.name);
    if (namesOfExperiences.some((name) => name === exp.title)) {
      return ExperienceType.MODERATES;
    }
    return ExperienceType.FOLLOWING;
  }

  private isNormalUser(
    oleConfig: OleConfiguration,
    experiencesArray: {
      externalUrl: string;
      learningExperienceId: string;
      title: string;
    }[]
  ) {
    return (
      oleConfig.canCreateExperiences === false &&
      oleConfig.canCreateUserGroups === false &&
      experiencesArray.length === 1
    );
  }

  private isCollaboratorUser(
    oleConfig: OleConfiguration,
    experiencesArray: {
      externalUrl: string;
      learningExperienceId: string;
      title: string;
    }[]
  ) {
    return (
      oleConfig.canCreateExperiences === false &&
      oleConfig.canCreateUserGroups === true &&
      experiencesArray.length === 1
    );
  }

  private openDialog(link) {
    if (!this.record.container) {
      this.rhId = null;
    }

    if (this.rssItem != null) {
      this.rhId = this.rssItem.rhId;
    }

    const externalVideoModel: ExternalVideoModel = {
      title: this.record.title,
      link: link,
      format: this.record.formats,
      userId: this.userService.getUserId(),
      resourceId: this.record.id,
      isFromExternalDb: false,
      checkoutId: this.checkoutsFacade.getCheckoutByRecordId(this.record.id, this.issueDate, this.rhId).id,
    };

    this.externalVideoDialogFacade.openDialog(externalVideoModel);
  }

  private extractToken(url: string): string {
    const downloadUrl = url.replace(/^(https?:|)\/\//, '');
    const parsedUrl: UrlTree = this.router.parseUrl(downloadUrl);

    return parsedUrl.queryParams.token;
  }

  resolvedCaptcha(token: string) {
    if (token) {
      this.recaptchaDialog.closeDialog();
      this.internalDownload(token);
    }
  }
}
